import React from "react";
import {Box, Grid2 as Grid, Pagination, Paper, Typography, useTheme} from "@mui/material";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {useTranslation} from "react-i18next";
import {CommonI18N, OrderI18N, RequestI18N} from "@/translations";
import {QuoteDTO, RequestActions} from "@/api/data-contracts";
import {QuoteStatusBadge} from "@/common/QuoteStatusBadge";
import BlockIcon from "@mui/icons-material/Block";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import {HakenIcon, XIcon} from "@/agentur/AgenturIcons";

interface ExternalRequestBannerProps {
    displayedModel: QuoteDTO;
    model: QuoteDTO;
    processing: boolean;
    hasErrors: boolean;
    editingRevision: boolean;
    allVersions: QuoteDTO[];
    versionIdx: number;
    setVersionIdx: (idx: number) => void;
    onSubmitQuote: () => void;
    onRejectRequest: () => void;
    onWithdrawQuote: () => void;
    onRevokeRequest: () => void;
    onSubmitRevisedBid: () => void;
}

/**
 * Banner displayed on the "Anfrage" tab of the external request page when tabs are visible.
 *
 * Left side: "Anfrage" label, BoxHub number, QuoteStatusBadge, version pagination.
 * Right side: Quote action buttons (send, reject, withdraw, revoke, revise).
 */
const ExternalRequestBanner: React.FC<ExternalRequestBannerProps> = ({
    displayedModel,
    model,
    processing,
    hasErrors,
    editingRevision,
    allVersions,
    versionIdx,
    setVersionIdx,
    onSubmitQuote,
    onRejectRequest,
    onWithdrawQuote,
    onRevokeRequest,
    onSubmitRevisedBid
}) => {
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <Paper elevation={3} sx={{ p: 2, mb: 2, width: "100%" }}>
            <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                <Grid>
                    <Typography variant="subtitle2" color="text.secondary">
                        {t(OrderI18N.tabs.tab.request)}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                        {displayedModel?.requestDisplay?.boxhubNumber ?? t(CommonI18N.message.notGiven)}
                    </Typography>
                    <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
                        <QuoteStatusBadge status={displayedModel?.status} />
                        {allVersions.length > 1 && (
                            <Pagination
                                count={allVersions.length}
                                page={versionIdx + 1}
                                variant="outlined"
                                shape="rounded"
                                size="small"
                                onChange={(_, page) => setVersionIdx(page - 1)}
                            />
                        )}
                    </Box>
                </Grid>

                <Grid>
                    <Grid container spacing={1}>
                        {model.requestDisplay?.requestActions?.includes(RequestActions.WITHDRAW_QUOTE) && (
                            <ButtonEx
                                data-cy="withdraw-button"
                                label={t(RequestI18N.details.action.withdrawQuote)}
                                color="primary"
                                startIcon={<BlockIcon />}
                                onClick={onWithdrawQuote}
                            />
                        )}
                        {model.requestDisplay?.requestActions?.includes(RequestActions.BID_FOR_REQUEST) && (
                            <ButtonEx
                                data-cy="send-quote-button"
                                label={t(RequestI18N.details.action.sendQuote)}
                                color="success"
                                startIcon={<HakenIcon mode={theme.palette.mode} />}
                                disabled={processing || hasErrors}
                                onClick={onSubmitQuote}
                            />
                        )}
                        {model.requestDisplay?.requestActions?.includes(RequestActions.REJECT_REQUEST) && (
                            <ButtonEx
                                data-cy="reject-request-button"
                                label={t(RequestI18N.details.action.rejectRequest)}
                                color="error"
                                startIcon={<XIcon mode={theme.palette.mode} />}
                                disabled={processing}
                                onClick={onRejectRequest}
                            />
                        )}
                        {model.requestDisplay?.requestActions?.includes(RequestActions.REVOKE_REJECTED_QUOTE) && (
                            <ButtonEx
                                data-cy="revoke-request-button"
                                label={t(RequestI18N.details.action.revokeRequest)}
                                color="success"
                                startIcon={<AutorenewIcon />}
                                disabled={processing}
                                onClick={onRevokeRequest}
                            />
                        )}
                        {editingRevision &&
                            !hasErrors &&
                            model.requestDisplay?.requestActions?.includes(RequestActions.SUBMIT_REVISED_BID) && (
                                <ButtonEx
                                    data-cy="submit-revised-bid-button"
                                    label={t(RequestI18N.details.action.submitRevisedBid)}
                                    color="success"
                                    startIcon={<HakenIcon mode={theme.palette.mode} />}
                                    disabled={processing}
                                    onClick={onSubmitRevisedBid}
                                />
                            )}
                    </Grid>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default ExternalRequestBanner;
